import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import { FC, ReactNode, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { SocketProvider, TablesLiveSync } from '../realtime'
import { useAppSelector } from '../redux-store/hooks'
import Header from './Header'
import SideBar from './SideBar'
import TitleBar, { TITLEBAR_HEIGHT } from './TitleBar'

const isElectron = typeof window !== 'undefined' && !!window.electronAPI

interface LayoutProps {
  children?: ReactNode
  isLoaderActive: boolean
  toggleTheme: () => void
  increaseFontSize: () => void
  decreaseFontSize: () => void
  canIncreaseFontSize: boolean
  canDecreaseFontSize: boolean
  selectedServerUrl: string | null
  onServerChange: (serverUrl: string) => void
}

const Layout: FC<LayoutProps> = ({
  children,
  isLoaderActive,
  toggleTheme,
  increaseFontSize,
  decreaseFontSize,
  canIncreaseFontSize,
  canDecreaseFontSize,
  selectedServerUrl,
  onServerChange,
}) => {
  const [open, setOpen] = useState(false)
  const HEADER_HEIGHT = useAppSelector((state) => state.ui.HEADER_HEIGHT)
  const titleBarOffset = isElectron ? TITLEBAR_HEIGHT : 0
  const chromeHeight = isElectron ? titleBarOffset : HEADER_HEIGHT

  const handleDrawerOpen = () => setOpen(true)
  const handleDrawerClose = () => setOpen(false)

  return (
    <SocketProvider apiBaseUrl={selectedServerUrl || undefined}>
      <TablesLiveSync />
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <TitleBar
          toggleTheme={toggleTheme}
          increaseFontSize={increaseFontSize}
          decreaseFontSize={decreaseFontSize}
          canIncrease={canIncreaseFontSize}
          canDecrease={canDecreaseFontSize}
          handleDrawerOpen={handleDrawerOpen}
          isLoaderActive={isLoaderActive}
          selectedServerUrl={selectedServerUrl}
          onServerChange={onServerChange}
        />
        {!isElectron && (
          <Header
            open={open}
            handleDrawerOpen={handleDrawerOpen}
            isLoaderActive={isLoaderActive}
            titleBarOffset={titleBarOffset}
          />
        )}
        <SideBar
          open={open}
          handleDrawerClose={handleDrawerClose}
          toggleTheme={toggleTheme}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            height: `calc(100vh - ${chromeHeight}px)`,
            marginTop: `${chromeHeight}px`,
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </SocketProvider>
  )
}

export { Layout }
